import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCircleDashed,
  IconMinus,
  IconPencil,
  IconPlus,
} from "@tabler/icons-react";
import { TeamFlag } from "@/components/team-flag";
import {
  DAY_MAX,
  DAY_MIN,
  DEFAULT_DURATION_MIN,
  DEFAULT_WINDOW_END,
  DEFAULT_WINDOW_START,
  PX_PER_MIN,
  SNAP_MIN,
  TASK_ICON,
  TASK_REQUIREMENTS,
  WINDOW_STEP_MIN,
  dateFromKey,
  dayPlusMinutesToNaive,
  formatSlotWhen,
  localDateKey,
  localMinutesOfDay,
  nowInZone,
  scheduleStatusMeta,
  snapMinutes,
  taskTypeShort,
} from "@/domain/schedule";
import { asScheduleSlotId } from "@/types/identifiers";
import type { ScheduleSlot, ScheduleTeamEntry } from "@/api/hooks/use-schedule";
import type { ScheduleActions } from "@/hooks/use-schedule-actions";

// Team label column width lives in the --label-w CSS var on the grid wrapper
// (flag-only 48px on phones, flag + code 120px from sm up) so the axis spacer,
// row labels, now-line and detail-row indent all track the same breakpoint.
const ROW_H = 48;
const MIN_HOUR_W = 64; // px-per-hour floor; the track scrolls below this, fills above it

// The visible hour window. `start`/`end` are minutes-of-day; `total` their span.
interface Range {
  start: number;
  end: number;
  total: number;
}

// Positions are percentages of the (fluid) track, relative to the current window.
const leftPct = (min: number, r: Range) => `${((min - r.start) / r.total) * 100}%`;
const widthPct = (mins: number, r: Range) => `${(mins / r.total) * 100}%`;
const clampStart = (min: number, r: Range) =>
  Math.max(r.start, Math.min(min, r.end - DEFAULT_DURATION_MIN));

const floorHour = (m: number) => Math.floor(m / 60) * 60;
const ceilHour = (m: number) => Math.ceil(m / 60) * 60;
const fmtHour = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function dayLabel(key: string): string {
  return dateFromKey(key).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function addDays(key: string, delta: number): string {
  const dt = dateFromKey(key);
  dt.setDate(dt.getDate() + delta);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function tileLabels(key: string): { wd: string; dn: number } {
  const dt = dateFromKey(key);
  return {
    wd: dt.toLocaleDateString("en-US", { weekday: "short" }),
    dn: dt.getDate(),
  };
}

export function SchedulePlanner({
  teams,
  canManage,
  canViewBuilds,
  actions,
  eventId,
  eventStart,
  eventEnd,
  timeZone,
  onEditSlot,
  onCreateAt,
  initialDayKey,
}: {
  teams: ScheduleTeamEntry[];
  canManage: boolean;
  canViewBuilds: boolean;
  actions: ScheduleActions;
  eventId: string;
  eventStart?: string;
  eventEnd?: string;
  timeZone?: string;
  onEditSlot: (slot: ScheduleSlot) => void;
  onCreateAt: (buildId: string, startIso: string) => void;
  initialDayKey: string;
}) {
  const [dayKey, setDayKey] = useState(initialDayKey);
  // Managers can flip back to the stable team order while arranging a day;
  // read-only users always get the chronological flow.
  const [rowOrder, setRowOrder] = useState<"time" | "team">("time");
  // User-chosen visible window (minutes-of-day); the rendered range widens past
  // it to include any slot scheduled outside it.
  const [win, setWin] = useState({ start: DEFAULT_WINDOW_START, end: DEFAULT_WINDOW_END });
  // Re-render each minute so the "now" marker tracks the current time.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const unscheduled = teams.reduce((n, t) => n + t.slots.filter((s) => !s.scheduled_start).length, 0);

  // Chronological rows: earliest slot of the selected day first; teams with
  // nothing scheduled that day sink to the bottom in team order.
  const firstStart = (t: ScheduleTeamEntry): number => {
    let min = DAY_MAX + 1;
    for (const s of t.slots) {
      if (s.scheduled_start && localDateKey(s.scheduled_start) === dayKey) {
        min = Math.min(min, localMinutesOfDay(s.scheduled_start));
      }
    }
    return min;
  };
  const orderedTeams =
    canManage && rowOrder === "team"
      ? teams
      : [...teams].sort((a, b) => firstStart(a) - firstStart(b));

  const dayCounts = new Map<string, number>();
  for (const t of teams) {
    for (const s of t.slots) {
      if (s.scheduled_start) {
        const k = localDateKey(s.scheduled_start);
        dayCounts.set(k, (dayCounts.get(k) ?? 0) + 1);
      }
    }
  }

  // Span of the selected day's slots — the rendered window is never narrower
  // than this, so a slot outside the chosen hours is still shown.
  let slotMin = Infinity;
  let slotMax = -Infinity;
  for (const t of teams) {
    for (const s of t.slots) {
      if (s.scheduled_start && localDateKey(s.scheduled_start) === dayKey) {
        const st = localMinutesOfDay(s.scheduled_start);
        slotMin = Math.min(slotMin, st);
        slotMax = Math.max(slotMax, st + (s.duration_minutes ?? DEFAULT_DURATION_MIN));
      }
    }
  }
  const hasSlots = slotMax > -Infinity;
  // The window may never hide a slot or the 09–17 core: the latest the start can
  // sit / the earliest the end can sit.
  const maxStart = Math.min(
    DEFAULT_WINDOW_START,
    hasSlots ? floorHour(slotMin) : DEFAULT_WINDOW_START,
  );
  const minEnd = Math.max(DEFAULT_WINDOW_END, hasSlots ? ceilHour(slotMax) : DEFAULT_WINDOW_END);
  const rStart = Math.max(DAY_MIN, Math.min(win.start, maxStart));
  const rEnd = Math.min(DAY_MAX, Math.max(win.end, minEnd));
  const range: Range = { start: rStart, end: rEnd, total: rEnd - rStart };

  const hours: number[] = [];
  for (let h = range.start; h <= range.end; h += 60) hours.push(h);
  const minTrackW = (range.total / 60) * MIN_HOUR_W;

  const { dateKey: today, minutes: nowMin } = nowInZone(timeZone);
  const showNow = dayKey === today && nowMin >= range.start && nowMin <= range.end;

  // Steppers move the *shown* bound by an hour — so a click always changes the
  // view even when a slot is holding the window open past it — then render
  // re-clamps. Neither side contracts past the 09–17 core or a slot.
  const startEarlier = () =>
    setWin((w) => ({ ...w, start: Math.max(DAY_MIN, range.start - WINDOW_STEP_MIN) }));
  const startLater = () =>
    setWin((w) => ({ ...w, start: Math.min(DEFAULT_WINDOW_START, range.start + WINDOW_STEP_MIN) }));
  const endEarlier = () =>
    setWin((w) => ({ ...w, end: Math.max(DEFAULT_WINDOW_END, range.end - WINDOW_STEP_MIN) }));
  const endLater = () =>
    setWin((w) => ({ ...w, end: Math.min(DAY_MAX, range.end + WINDOW_STEP_MIN) }));

  // The day picker spans exactly the event window; fall back to scheduled days
  // + today only when the event has no dates.
  const slotDays = [...new Set([today, ...dayCounts.keys()])].sort();
  const dayRangeStart = eventStart ?? slotDays[0] ?? today;
  const dayRangeEnd = eventEnd ?? slotDays[slotDays.length - 1] ?? today;
  const rangeDays: string[] = [];
  for (let d = dayRangeStart, i = 0; d <= dayRangeEnd && i < 120; d = addDays(d, 1), i++) {
    rangeDays.push(d);
  }
  const stepDay = (delta: number) =>
    setDayKey((d) => {
      const n = addDays(d, delta);
      return n < dayRangeStart ? dayRangeStart : n > dayRangeEnd ? dayRangeEnd : n;
    });

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-muted-foreground">{dayLabel(dayKey)}</div>
            {canManage && (
              <div className="inline-flex rounded-md border border-border p-0.5 text-[10px]">
                {(["time", "team"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setRowOrder(v)}
                    className={`rounded px-1.5 py-0.5 capitalize ${
                      rowOrder === v
                        ? "bg-muted font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] tabular-nums text-muted-foreground">
            <div className="flex items-center gap-0.5">
              <button
                onClick={startEarlier}
                disabled={range.start <= DAY_MIN}
                className="rounded p-1 hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Show an earlier start hour"
              >
                <IconMinus className="h-3.5 w-3.5" />
              </button>
              <span className="w-10 text-center">{fmtHour(range.start)}</span>
              <button
                onClick={startLater}
                disabled={range.start >= maxStart}
                className="rounded p-1 hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Trim the start hour"
              >
                <IconPlus className="h-3.5 w-3.5" />
              </button>
            </div>
            <span className="text-muted-foreground/40">–</span>
            <div className="flex items-center gap-0.5">
              <button
                onClick={endEarlier}
                disabled={range.end <= minEnd}
                className="rounded p-1 hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Trim the end hour"
              >
                <IconMinus className="h-3.5 w-3.5" />
              </button>
              <span className="w-10 text-center">{fmtHour(range.end)}</span>
              <button
                onClick={endLater}
                disabled={range.end >= DAY_MAX}
                className="rounded p-1 hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Show a later end hour"
              >
                <IconPlus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto pb-1">
          <div className="mx-auto flex w-max items-center gap-1">
            <button
              onClick={() => stepDay(-1)}
              className="shrink-0 rounded-md p-1.5 hover:bg-muted"
              aria-label="Previous day"
            >
              <IconChevronLeft className="h-4 w-4" />
            </button>
            {rangeDays.map((d) => {
              const { wd, dn } = tileLabels(d);
              const active = d === dayKey;
              const count = dayCounts.get(d) ?? 0;
              return (
                <button
                  key={d}
                  onClick={() => setDayKey(d)}
                  className={`flex w-11 shrink-0 flex-col items-center rounded-md border py-1 leading-tight transition-colors ${
                    active ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                  }`}
                >
                  <span className="text-[10px] uppercase text-muted-foreground">{wd}</span>
                  <span
                    className={`flex h-5 w-5 items-center justify-center text-sm font-semibold ${
                      d === today ? "rounded-full bg-primary text-primary-foreground" : ""
                    }`}
                  >
                    {dn}
                  </span>
                  <span
                    className={`text-[9px] font-medium ${count > 0 ? "text-primary" : "text-transparent"}`}
                  >
                    {count > 0 ? count : "0"}
                  </span>
                </button>
              );
            })}
            <button
              onClick={() => stepDay(1)}
              className="shrink-0 rounded-md p-1.5 hover:bg-muted"
              aria-label="Next day"
            >
              <IconChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border border-border">
        <div
          className="relative w-full bg-card [--label-w:48px] sm:[--label-w:120px]"
          style={{ minWidth: `calc(var(--label-w) + ${minTrackW}px)` }}
        >
          {/* Hour axis */}
          <div className="flex border-b border-border bg-muted/30" style={{ height: 22 }}>
            <div
              style={{ width: "var(--label-w)" }}
              className="sticky left-0 z-20 shrink-0 border-r border-border/60 bg-card"
            >
              <div className="absolute inset-0 bg-muted/30" />
            </div>
            <div className="relative flex-1">
              {hours.map((h) => {
                const tx =
                  h === range.start
                    ? "translate-x-0"
                    : h === range.end
                      ? "-translate-x-full"
                      : "-translate-x-1/2";
                return (
                  <div
                    key={h}
                    className={`absolute top-0 flex h-full items-center px-1 text-[10px] text-muted-foreground ${tx}`}
                    style={{ left: leftPct(h, range) }}
                  >
                    {fmtHour(h)}
                  </div>
                );
              })}
            </div>
          </div>

          {orderedTeams.map((team) => (
            <PlannerRow
              key={team.event_build_id}
              team={team}
              dayKey={dayKey}
              range={range}
              canManage={canManage}
              canViewBuilds={canViewBuilds}
              actions={actions}
              eventId={eventId}
              hours={hours}
              onEditSlot={onEditSlot}
              onCreateAt={onCreateAt}
            />
          ))}

          {showNow && (
            <div
              className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-red-500"
              style={{
                left: `calc(var(--label-w) + (100% - var(--label-w)) * ${
                  (nowMin - range.start) / range.total
                })`,
              }}
            >
              <span className="absolute -left-[3px] -top-[3px] h-1.5 w-1.5 rounded-full bg-red-500" />
            </div>
          )}
        </div>
      </div>

      {unscheduled > 0 && (
        <p className="text-xs text-muted-foreground">
          {unscheduled} unscheduled {unscheduled === 1 ? "slot" : "slots"} not shown — open one to
          set a time.
        </p>
      )}
    </div>
  );
}

function PlannerRow({
  team,
  dayKey,
  range,
  canManage,
  canViewBuilds,
  actions,
  eventId,
  hours,
  onEditSlot,
  onCreateAt,
}: {
  team: ScheduleTeamEntry;
  dayKey: string;
  range: Range;
  canManage: boolean;
  canViewBuilds: boolean;
  actions: ScheduleActions;
  eventId: string;
  hours: number[];
  onEditSlot: (slot: ScheduleSlot) => void;
  onCreateAt: (buildId: string, startIso: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [ghostMin, setGhostMin] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const daySlots = team.slots.filter(
    (s) => s.scheduled_start && localDateKey(s.scheduled_start) === dayKey,
  );
  const expandedSlot = daySlots.find((s) => s.id === expandedId) ?? null;

  function minFromClientX(clientX: number): number | null {
    const el = trackRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return null;
    return clampStart(
      snapMinutes(range.start + ((clientX - rect.left) / rect.width) * range.total),
      range,
    );
  }

  function overSlot(min: number): boolean {
    return daySlots.some((s) => {
      const st = localMinutesOfDay(s.scheduled_start as string);
      const en = st + (s.duration_minutes ?? DEFAULT_DURATION_MIN);
      return min >= st && min < en;
    });
  }

  function onMove(e: React.PointerEvent) {
    if (!canManage || e.buttons !== 0) {
      setGhostMin(null);
      return;
    }
    const min = minFromClientX(e.clientX);
    setGhostMin(min != null && !overSlot(min) ? min : null);
  }

  function onCreate(e: React.MouseEvent) {
    // Existing slots stopPropagation their click, so this only fires on empty track.
    if (!canManage) return;
    const min = minFromClientX(e.clientX);
    if (min == null || overSlot(min)) return;
    onCreateAt(team.event_build_id, dayPlusMinutesToNaive(dayKey, min));
  }

  return (
    <>
      <div className="flex border-b border-border/50" style={{ height: ROW_H }}>
        {canViewBuilds ? (
          <Link
            to={`/events/${eventId}/builds/${team.event_build_id}`}
            style={{ width: "var(--label-w)" }}
            className="sticky left-0 z-20 flex shrink-0 items-center justify-center gap-2 border-r border-border/60 bg-card px-2 hover:bg-muted/40 sm:justify-start sm:px-3"
            title={`Open ${team.team_code} build`}
          >
            <TeamFlag iso_alpha2={team.team_iso_alpha2} size="xs" />
            <span className="hidden text-xs font-semibold hover:underline sm:inline">
              {team.team_code}
            </span>
          </Link>
        ) : (
          <div
            style={{ width: "var(--label-w)" }}
            className="sticky left-0 z-20 flex shrink-0 items-center justify-center gap-2 border-r border-border/60 bg-card px-2 sm:justify-start sm:px-3"
          >
            <TeamFlag iso_alpha2={team.team_iso_alpha2} size="xs" />
            <span className="hidden text-xs font-semibold sm:inline">{team.team_code}</span>
          </div>
        )}
        <div
          ref={trackRef}
          data-track
          className={`relative flex-1 ${canManage ? "cursor-copy" : ""}`}
          onPointerMove={onMove}
          onPointerLeave={() => setGhostMin(null)}
          onClick={onCreate}
        >
          {hours.map((h) => (
            <div
              key={h}
              className="absolute bottom-0 top-0 border-l border-border/40"
              style={{ left: leftPct(h, range) }}
            />
          ))}

          {ghostMin != null && (
            <div
              className="pointer-events-none absolute bottom-1 top-1 flex items-center justify-center rounded-md border border-dashed border-foreground/30 bg-foreground/5"
              style={{ left: leftPct(ghostMin, range), width: widthPct(DEFAULT_DURATION_MIN, range) }}
            >
              <IconPlus className="h-3.5 w-3.5 text-foreground/40" />
            </div>
          )}

          {daySlots.map((s) => (
            <PlannerBlock
              key={s.id}
              slot={s}
              range={range}
              canManage={canManage}
              dayKey={dayKey}
              actions={actions}
              expanded={expandedId === s.id}
              onToggleExpand={() => setExpandedId((id) => (id === s.id ? null : s.id))}
              onEdit={() => onEditSlot(s)}
            />
          ))}
        </div>
      </div>

      {expandedSlot && (
        <SlotDetailRow slot={expandedSlot} eventId={eventId} canViewBuilds={canViewBuilds} />
      )}
    </>
  );
}

function PlannerBlock({
  slot,
  range,
  canManage,
  dayKey,
  actions,
  expanded,
  onToggleExpand,
  onEdit,
}: {
  slot: ScheduleSlot;
  range: Range;
  canManage: boolean;
  dayKey: string;
  actions: ScheduleActions;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
}) {
  const start0 = localMinutesOfDay(slot.scheduled_start as string);
  const dur0 = slot.duration_minutes ?? DEFAULT_DURATION_MIN;
  const [draft, setDraft] = useState<{ start: number; dur: number } | null>(null);

  const start = draft?.start ?? start0;
  const dur = draft?.dur ?? dur0;
  const meta = scheduleStatusMeta(slot.status);
  const TaskIcon = TASK_ICON[slot.task_type];

  function startGesture(mode: "move" | "resize", e: React.PointerEvent) {
    if (!canManage) return;
    e.preventDefault();
    e.stopPropagation();
    // Track is fluid — derive px-per-minute from its measured width.
    const track = e.currentTarget.closest<HTMLElement>("[data-track]");
    const pxPerMin = (track?.clientWidth || range.total * PX_PER_MIN) / range.total;
    const startX = e.clientX;
    let moved = false;
    let latest = { start: start0, dur: dur0 };

    const onPointerMove = (ev: PointerEvent) => {
      const deltaMin = snapMinutes((ev.clientX - startX) / pxPerMin);
      if (Math.abs(ev.clientX - startX) > 3) moved = true;
      if (mode === "move") {
        const ns = Math.max(range.start, Math.min(start0 + deltaMin, range.end - dur0));
        latest = { start: ns, dur: dur0 };
      } else {
        const nd = Math.max(SNAP_MIN, Math.min(dur0 + deltaMin, range.end - start0));
        latest = { start: start0, dur: nd };
      }
      setDraft(latest);
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      setDraft(null);
      // A plain click no longer edits — editing is behind the pencil icon.
      if (!moved) return;
      if (mode === "move") {
        void actions.handleUpdate(asScheduleSlotId(slot.id), {
          scheduled_start: dayPlusMinutesToNaive(dayKey, latest.start),
        });
      } else {
        void actions.handleUpdate(asScheduleSlotId(slot.id), { duration_minutes: latest.dur });
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  return (
    <div
      className={`absolute bottom-1 top-1 flex touch-none select-none flex-col justify-center overflow-hidden rounded-r border border-border border-l-3 bg-card ${meta.border} ${
        canManage ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      style={{ left: leftPct(start, range), width: widthPct(dur, range), minWidth: 16 }}
      onPointerDown={(e) => startGesture("move", e)}
      onClick={(e) => e.stopPropagation()}
      title={`${taskTypeShort(slot.task_type)} — ${meta.label}`}
    >
      <div className="flex items-center">
        <TaskIcon className="ml-1.5 h-3.5 w-3.5 shrink-0" />
        <span className="ml-auto mr-1.5 flex shrink-0 items-center">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="rounded p-0.5 hover:bg-foreground/10"
            aria-label="Slot details"
          >
            <IconChevronDown
              className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
          {canManage && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="rounded p-0.5 hover:bg-foreground/10"
              aria-label="Edit slot"
            >
              <IconPencil className="h-3 w-3" />
            </button>
          )}
        </span>
      </div>

      <span className="truncate px-1.5 text-[10px] font-medium leading-tight">
        {taskTypeShort(slot.task_type)}
      </span>

      {canManage && (
        <span
          onPointerDown={(e) => startGesture("resize", e)}
          className="absolute bottom-0 right-0 top-0 w-1.5 cursor-ew-resize hover:bg-foreground/20"
        />
      )}
    </div>
  );
}

function SlotDetailRow({
  slot,
  eventId,
  canViewBuilds,
}: {
  slot: ScheduleSlot;
  eventId: string;
  canViewBuilds: boolean;
}) {
  const meta = scheduleStatusMeta(slot.status);
  const DetailIcon = TASK_ICON[slot.task_type];
  const requirements = TASK_REQUIREMENTS[slot.task_type] ?? [];
  const signoffByKind = new Map(slot.signoffs.map((s) => [s.kind, s]));

  return (
    <div
      className={`border-b border-l-3 border-border/50 bg-muted/20 py-2.5 pr-4 text-xs ${meta.border}`}
      style={{ paddingLeft: "calc(var(--label-w) + 12px)" }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 font-semibold">
          <DetailIcon className="h-3.5 w-3.5" />
          {taskTypeShort(slot.task_type)}
        </span>
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.bg} ${meta.text}`}
        >
          {meta.label}
        </span>
        <span className="text-muted-foreground">
          {formatSlotWhen(slot.scheduled_start)}
          {slot.duration_minutes ? ` · ${slot.duration_minutes} min` : ""}
        </span>
        {canViewBuilds && (
          <Link
            to={`/events/${eventId}/builds/${slot.event_build_id}`}
            className="ml-auto inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
          >
            Open build
            <IconArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>

      <div className="mt-2 space-y-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Requirements
        </div>
        {requirements.map((req) => {
          const done = signoffByKind.get(req.kind);
          return (
            <div key={req.kind} className="flex flex-wrap items-baseline gap-x-2">
              {done ? (
                <IconCircleCheck className="h-3.5 w-3.5 shrink-0 self-center text-emerald-600 dark:text-emerald-400" />
              ) : (
                <IconCircleDashed className="h-3.5 w-3.5 shrink-0 self-center text-muted-foreground/60" />
              )}
              <span className="font-medium">{req.label}</span>
              <span className="text-muted-foreground">
                {done
                  ? `${done.signed_off_at ? formatSlotWhen(done.signed_off_at) : "signed off"}${
                      done.signed_off_by ? ` · ${done.signed_off_by}` : ""
                    }`
                  : "outstanding"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
